import React, { useState, useEffect, useCallback } from "react";
import { useErrorNotification } from "../../context/ErrorNotificationContext";
import {
  fetchAdminUsers,
  updateUserRole,
  fetchUserAgentAccess,
  grantAgentAccess,
  revokeAgentAccess,
  grantAllAgentAccess,
  revokeAllAgentAccess,
  fetchAgents,
  addUser,
  deleteUser,
  AdminUser,
  UserAgentAccess,
  AgentApiDto,
} from "../../api/api";
import {
  PersonAdd20Regular,
  Search20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Delete20Regular,
  Shield20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  People20Regular,
} from "@fluentui/react-icons";
import styles from "./UserManagement.module.css";

// Product Knowledge Base is auto-granted; hide from agent management UI
const PKB_AGENT_ID = "a1000000-0000-0000-0000-000000000001";

interface UserManagementProps {
  currentUserId: string;
}

const ROLES = [
  { key: "Administrator", label: "Administrator" },
  { key: "ProductManager", label: "Product Manager" },
  { key: "User", label: "User" },
];

export const UserManagement: React.FC<UserManagementProps> = ({ currentUserId }) => {
  const { showError } = useErrorNotification();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [agents, setAgents] = useState<AgentApiDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userAgentAccess, setUserAgentAccess] = useState<UserAgentAccess[]>([]);
  const [agentAccessLoading, setAgentAccessLoading] = useState(false);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Add user dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addName, setAddName] = useState("");
  const [addRole, setAddRole] = useState("User");
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);

  // Delete user dialog
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAdminUsers();
      setUsers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const data = await fetchAgents();
      setAgents(data.filter((a) => a.agentId !== PKB_AGENT_ID));
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadAgents();
  }, [loadUsers, loadAgents]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (userId === currentUserId) return;
    setUpdatingRole(userId);
    try {
      await updateUserRole(userId, newRole);
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to update role");
    } finally {
      setUpdatingRole(null);
    }
  };

  const toggleExpand = async (userId: string) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    setAgentAccessLoading(true);
    try {
      const accesses = await fetchUserAgentAccess(userId);
      setUserAgentAccess(accesses.filter((a) => a.agentId !== PKB_AGENT_ID));
    } catch {
      setUserAgentAccess([]);
    } finally {
      setAgentAccessLoading(false);
    }
  };

  const handleGrantAgent = async (userId: string, agentId: string) => {
    try {
      await grantAgentAccess(userId, [agentId]);
      const accesses = await fetchUserAgentAccess(userId);
      setUserAgentAccess(accesses.filter((a) => a.agentId !== PKB_AGENT_ID));
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to grant access");
    }
  };

  const handleRevokeAgent = async (userId: string, agentId: string) => {
    try {
      await revokeAgentAccess(userId, agentId);
      const accesses = await fetchUserAgentAccess(userId);
      setUserAgentAccess(accesses.filter((a) => a.agentId !== PKB_AGENT_ID));
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to revoke access");
    }
  };

  const handleGrantAll = async (userId: string) => {
    try {
      await grantAllAgentAccess(userId);
      const accesses = await fetchUserAgentAccess(userId);
      setUserAgentAccess(accesses.filter((a) => a.agentId !== PKB_AGENT_ID));
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to grant all access");
    }
  };

  const handleRevokeAll = async (userId: string) => {
    try {
      await revokeAllAgentAccess(userId);
      setUserAgentAccess([]);
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to revoke all access");
    }
  };

  const handleAddUser = async () => {
    if (!addEmail.trim()) {
      setAddError("Email is required.");
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await addUser(addEmail.trim(), addName.trim() || null, addRole);
      setShowAddDialog(false);
      setAddEmail("");
      setAddName("");
      setAddRole("User");
      await loadUsers();
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "Failed to add user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteUser(deleteTarget.userId);
      setDeleteTarget(null);
      if (expandedUserId === deleteTarget.userId) setExpandedUserId(null);
      await loadUsers();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };



  /** Derive a display name from the email local part (e.g. "diwakar.rai" → "Diwakar Rai") */
  const deriveNameFromEmail = (email: string): string | null => {
    const local = email.split("@")[0];
    const parts = local.split(/[._-]+/).filter(Boolean);
    if (parts.length === 0) return null;
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
  };

  const getDisplayName = (user: AdminUser): string => {
    if (user.name) return user.name;
    if (user.email) return deriveNameFromEmail(user.email) ?? user.email;
    return user.userId;
  };

  const getInitials = (user: AdminUser): string => {
    const name = user.name || (user.email ? deriveNameFromEmail(user.email) : null);
    if (name) {
      const parts = name.split(/[\s.]+/).filter(Boolean);
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].substring(0, 2).toUpperCase();
    }
    return "??";
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <span>Loading users…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <span>{error}</span>
        <button className={styles.retryBtn} onClick={loadUsers}>Retry</button>
      </div>
    );
  }

  const accessedAgentIds = new Set(userAgentAccess.map((a) => a.agentId));

  const filteredUsers = search.trim()
    ? users.filter(
        (u) =>
          (u.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
          (u.email ?? "").toLowerCase().includes(search.toLowerCase()) ||
          deriveNameFromEmail(u.email ?? "")?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedUsers = filteredUsers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Search20Regular className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search users…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <button className={styles.addUserBtn} onClick={() => setShowAddDialog(true)}>
          <PersonAdd20Regular />
          Add User
        </button>
      </div>

      <p className={styles.description}>
        <People20Regular className={styles.descIcon} />
        Administrators have full access to all agents. Product Knowledge Base is available to all users by default.
      </p>

      {/* User cards */}
      <div className={styles.userList}>
        {filteredUsers.length === 0 && (
          <div className={styles.emptyState}>No users match your search.</div>
        )}
        {pagedUsers.map((user) => {
          const isCurrentUser = user.userId === currentUserId;
          const isExpanded = expandedUserId === user.userId;
          const isAdminUser = user.role === "Administrator";
          return (
            <div
              key={user.userId}
              className={`${styles.userCard} ${isExpanded ? styles.userCardExpanded : ""}`}
            >
              <div className={styles.userCardMain}>
                {/* Avatar */}
                <div className={`${styles.avatar} ${isAdminUser ? styles.avatarAdmin : ""}`}>
                  {getInitials(user)}
                </div>

                {/* Info */}
                <div className={styles.userInfo}>
                  <div className={styles.userNameRow}>
                    <span className={styles.userName}>
                      {getDisplayName(user)}
                    </span>
                    {isCurrentUser && <span className={styles.youBadge}>You</span>}
                  </div>
                  <span className={styles.userEmail}>{user.email || ""}</span>
                </div>


                  <select
                    className={styles.roleSelect}
                    value={user.role}
                    disabled={isCurrentUser || updatingRole === user.userId}
                    onChange={(e) => handleRoleChange(user.userId, e.target.value)}
                    title={isCurrentUser ? "Cannot change your own role" : ""}
                  >
                    {ROLES.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                

                {/* Agent access */}
                <div className={styles.agentCountCell}>
                  {isAdminUser ? (
                    <span className={styles.allAccessBadge}>
                      <Shield20Regular className={styles.badgeIcon} />
                      All
                    </span>
                  ) : (
                    <button
                      className={styles.agentCountBtn}
                      onClick={() => toggleExpand(user.userId)}
                      title="Manage agent access"
                    >
                      <span>
                        {user.agentAccessCount}{" "}
                        {user.agentAccessCount === 1 ? "agent" : "agents"}
                      </span>
                      {isExpanded ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
                    </button>
                  )}
                </div>

                {/* Delete */}
                <button
                  className={styles.deleteBtn}
                  onClick={() => setDeleteTarget(user)}
                  disabled={isCurrentUser}
                  title={isCurrentUser ? "Cannot delete yourself" : "Delete user"}
                >
                  <Delete20Regular />
                </button>
              </div>

              {/* Agent access panel */}
              {isExpanded && !isAdminUser && (
                <div className={styles.agentPanel}>
                  {agentAccessLoading ? (
                    <div className={styles.loadingState}>
                      <div className={styles.spinner} />
                      <span>Loading agents…</span>
                    </div>
                  ) : (
                    <>
                      <div className={styles.agentPanelHeader}>
                        <span className={styles.agentPanelTitle}>Agent Access</span>
                        <div className={styles.agentBulkActions}>
                          <button
                            className={styles.bulkBtn}
                            onClick={() => handleGrantAll(user.userId)}
                          >
                            <Checkmark20Regular />
                            Grant All
                          </button>
                          <button
                            className={`${styles.bulkBtn} ${styles.bulkBtnDanger}`}
                            onClick={() => handleRevokeAll(user.userId)}
                            disabled={accessedAgentIds.size === 0}
                          >
                            <Dismiss20Regular />
                            Revoke All
                          </button>
                        </div>
                      </div>
                      <div className={styles.agentGrid}>
                        {agents.map((agent) => {
                          const hasAccess = accessedAgentIds.has(agent.agentId);
                          return (
                            <div
                              key={agent.agentId}
                              className={`${styles.agentChip} ${
                                hasAccess ? styles.agentChipGranted : styles.agentChipRevoked
                              }`}
                            >
                              <span className={styles.agentName}>{agent.name}</span>
                              <button
                                className={`${styles.agentToggle} ${
                                  hasAccess ? styles.toggleRevoke : styles.toggleGrant
                                }`}
                                onClick={() =>
                                  hasAccess
                                    ? handleRevokeAgent(user.userId, agent.agentId)
                                    : handleGrantAgent(user.userId, agent.agentId)
                                }
                              >
                                {hasAccess ? "Revoke" : "Grant"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Pagination ── */}
      {filteredUsers.length > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {filteredUsers.length === 1
              ? "1 user"
              : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filteredUsers.length)} of ${filteredUsers.length} users`}
          </span>
          <div className={styles.paginationControls}>
            <button
              className={styles.pageBtn}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              title="Previous page"
            >
              <ChevronLeft20Regular />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`${styles.pageBtn} ${page === safePage ? styles.pageBtnActive : ""}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className={styles.pageBtn}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              title="Next page"
            >
              <ChevronRight20Regular />
            </button>
          </div>
        </div>
      )}

      {/* ── Add User Dialog ── */}
      {showAddDialog && (
        <div className={styles.dialogOverlay} onClick={() => setShowAddDialog(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <PersonAdd20Regular className={styles.dialogHeaderIcon} />
              <h3 className={styles.dialogTitle}>Add User</h3>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.dialogField}>
                <label className={styles.dialogLabel}>Email *</label>
                <input
                  className={styles.dialogInput}
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="user@company.com"
                  autoFocus
                />
              </div>
              <div className={styles.dialogField}>
                <label className={styles.dialogLabel}>Name</label>
                <input
                  className={styles.dialogInput}
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Display name (optional)"
                />
              </div>
              <div className={styles.dialogField}>
                <label className={styles.dialogLabel}>Role</label>
                <select
                  className={styles.roleSelect}
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value)}
                >
                  {ROLES.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>
              {addError && <div className={styles.dialogError}>{addError}</div>}
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.dialogSecondary} onClick={() => setShowAddDialog(false)}>
                Cancel
              </button>
              <button
                className={styles.dialogPrimary}
                onClick={handleAddUser}
                disabled={addLoading || !addEmail.trim()}
              >
                {addLoading ? "Adding…" : "Add User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete User Dialog ── */}
      {deleteTarget && (
        <div className={styles.dialogOverlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <Delete20Regular className={styles.dialogHeaderIconDanger} />
              <h3 className={styles.dialogTitle}>Delete User</h3>
            </div>
            <div className={styles.dialogBody}>
              <p className={styles.dialogBodyText}>
                Are you sure you want to delete{" "}
                <strong>{deleteTarget.name || deleteTarget.email || deleteTarget.userId}</strong>?
                This will remove the user and all their agent access.
              </p>
            </div>
            <div className={styles.dialogActions}>
              <button className={styles.dialogSecondary} onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button
                className={styles.dialogDanger}
                onClick={handleDeleteUser}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
